import React, { useRef } from "react";
import { typography, spacing } from "../styles/designSystem";
import { getScrollVisibility, useScrollProgress } from "../styles/animations";
import { painPoints } from "../data/painPointsData";

const PainPoints = () => {
  const sectionRef = useRef(null);
  const progress = useScrollProgress(sectionRef);
  const visibility = getScrollVisibility(progress);

  return (
    <section
      id="pain-points"
      ref={sectionRef}
      className="relative overflow-hidden bg-[#f8f5ed] py-28 md:py-40"
    >
      {/* Decorative shapes */}
      <div
        className="animate-drift pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-primary/[0.07] blur-3xl will-change-transform"
        style={{
          transform: `translate(${(1 - visibility) * -40}px, ${(1 - visibility) * -25}px)`,
        }}
      />

      <div
        className="animate-float pointer-events-none absolute -right-16 bottom-16 h-56 w-56 rotate-12 rounded-[3rem] border border-[#9c1d14]/15 bg-[#9c1d14]/[0.045] will-change-transform"
        style={{
          transform: `translate(${(1 - visibility) * 35}px, ${
            (1 - visibility) * 25
          }px) rotate(${12 + (1 - visibility) * 8}deg)`,
        }}
      />

      <div
        className="pattern-dot-grid pointer-events-none absolute left-8 bottom-8 h-40 w-40 rounded-2xl"
        style={{ opacity: 0.35 * visibility }}
      />

      <div className={`relative ${spacing.container}`}>
        <div className="mx-auto max-w-5xl text-center">
          <p
            className={`${typography.label} text-gray-400`}
            style={{
              opacity: visibility,
              transform: `translateY(${(1 - visibility) * 20}px)`,
            }}
          >
            Sound familiar?
          </p>

          <h2
            className={`${typography.heading.h2} mx-auto mt-5 max-w-4xl text-secondary`}
            style={{
              opacity: visibility,
              transform: `translateY(${(1 - visibility) * 25}px)`,
            }}
          >
            Your business needs to look professional online, but{" "}
            <span className="text-red">building everything yourself</span> is
            overwhelming.
          </h2>

          <p
            className={`${typography.body.base} mx-auto mt-7 max-w-3xl`}
            style={{
              opacity: visibility,
              transform: `translateY(${(1 - visibility) * 30}px)`,
            }}
          >
            Many small businesses know they need a website or shop, but they do
            not have the time, technical skills, or clear strategy to make it
            happen properly.
          </p>
        </div>

        {/* Pain point cards */}
        <div className="mx-auto mt-20 grid max-w-6xl gap-7 md:grid-cols-2">
          {painPoints.map((item, index) => (
            <div
              key={item.title}
              className="group relative overflow-hidden rounded-3xl border border-secondary/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#9c1d14]/25 hover:shadow-xl md:p-10 will-change-transform"
              style={{
                opacity: visibility,
                transform: `translateY(${(1 - visibility) * (45 + index * 10)}px)`,
                transition: "transform 0.05s linear, opacity 0.12s linear",
                transitionDelay: `${index * 40}ms`,
              }}
            >
              <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br from-[#9c1d14]/[0.045] to-primary/[0.035] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

              <div className="relative flex gap-6">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#9c1d14]/10 text-2xl text-red transition-all duration-300 group-hover:rotate-6 group-hover:scale-110 group-hover:bg-[#9c1d14] group-hover:text-white md:h-16 md:w-16">
                  {item.icon}
                </span>

                <div>
                  <h3
                    className={`${typography.card.large} transition-colors group-hover:text-[#9c1d14]`}
                  >
                    {item.title}
                  </h3>

                  <p className={`${typography.body.base} mt-4`}>{item.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PainPoints;
